<template>
  <div class="cmp-editor" data-testid="campaigns">
    <!-- Left: sidebar — selector + goal form + chat (site-editor style) -->
    <aside class="cmp-side">
      <div class="cmp-side-top">
        <span class="cmp-side-title">Campaigns</span>
        <button class="cmp-btn cmp-btn-primary cmp-btn-sm" data-testid="campaign-new" @click="showForm = !showForm">{{ showForm ? 'Cancel' : '+ New' }}</button>
      </div>

      <!-- New campaign: goal only -->
      <section v-if="showForm" class="cmp-side-form" data-testid="campaign-form">
        <p class="cmp-form-lead">Set a goal — the AI picks the capability, topic &amp; angle from your site.</p>
        <label class="cmp-field">
          <span class="cmp-label">Goal — outcome to optimise</span>
          <select v-model="form.objective" class="cmp-input" data-testid="form-objective">
            <option value="engagement">Engagement</option>
            <option value="impressions">Impressions</option>
            <option value="followers">Followers</option>
          </select>
        </label>
        <div class="cmp-form-row">
          <label class="cmp-field">
            <span class="cmp-label">Target (optional)</span>
            <input v-model.number="form.targetValue" type="number" min="0" class="cmp-input" placeholder="200" data-testid="form-target" />
          </label>
          <label class="cmp-field">
            <span class="cmp-label">Within (days)</span>
            <input v-model.number="form.targetWindowDays" type="number" min="1" max="365" class="cmp-input" placeholder="30" />
          </label>
        </div>
        <label class="cmp-field">
          <span class="cmp-label">Anything to emphasise? (optional)</span>
          <textarea v-model.trim="form.detail" class="cmp-input cmp-textarea" rows="2" placeholder="Audience, tone, or a capability to favour — or leave blank." data-testid="form-detail"></textarea>
        </label>
        <button class="cmp-btn cmp-btn-primary" :disabled="store.creating" data-testid="form-submit" @click="submitCreate">
          {{ store.creating ? 'Creating…' : 'Create campaign' }}
        </button>
      </section>

      <p v-if="store.error" class="cmp-alert" data-testid="campaign-error">{{ store.error }}</p>

      <!-- Campaign selector -->
      <div v-if="store.list.length" class="cmp-side-list" data-testid="campaign-chips">
        <button
          v-for="c in store.list"
          :key="c.id"
          class="cmp-cchip"
          :class="{ active: c.id === selectedId }"
          :data-testid="`campaign-chip-${c.id}`"
          @click="openCampaign(c.id)"
        >
          <span class="cmp-cchip-dot" :data-status="c.status"></span>
          <span class="cmp-cchip-name">{{ c.topic || ('New ' + objectiveLabel(c.objective).toLowerCase() + ' campaign') }}</span>
          <span class="cmp-cchip-obj">{{ objectiveLabel(c.objective) }}</span>
        </button>
      </div>

      <!-- Chat fills the rest -->
      <div class="cmp-side-chat">
        <CampaignChat
          v-if="selectedId"
          :campaign="store.current"
          :reports="store.reports"
          :generating="store.generating"
          :scheduling="store.scheduling"
          :observing="store.observing"
          @generate="onGenerate"
          @approve="onApprove"
          @collect="onCollect"
          @select-post="showPostPopup"
        />
        <p v-else-if="!store.loadingList" class="cmp-side-empty">Set a goal above to start your first campaign.</p>
      </div>
    </aside>

    <!-- Right: main — results on top, calendar below -->
    <main class="cmp-main">
      <template v-if="selectedId">
        <CampaignResults :campaign="store.current" :reports="store.reports" />
        <section class="cmp-card cmp-cal-wrap" data-testid="campaign-calendar">
          <div class="cmp-section-label">Schedule</div>
          <CampaignCalendar :posts="calendarPosts" :loading="store.loadingCalendar" @select="showPostPopup" />
          <p v-if="!calendarPosts.length && !store.loadingCalendar" class="cmp-hint">
            No scheduled posts yet — generate + approve from the chat.
          </p>
        </section>
      </template>
      <div v-else class="cmp-main-empty">
        <p class="cmp-main-empty-title">No campaign selected</p>
        <p class="cmp-muted">Set a goal on the left — the AI drafts an omnichannel schedule, publishes it, watches performance, and recommends the next round.</p>
      </div>
    </main>

    <!-- Post preview popup -->
    <transition name="cmp-pop">
      <div v-if="postPopup" class="cmp-pop-backdrop" @click.self="postPopup = null">
        <div class="cmp-pop" role="dialog" aria-label="Post preview" data-testid="post-popup">
          <div class="cmp-pop-head">
            <span class="cmp-chip cmp-chip-channel">{{ postPopup.channel }}</span>
            <span v-if="postPopup.angle" class="cmp-pop-angle">{{ postPopup.angle }}</span>
            <span class="cmp-pill" :data-status="postPopup.status">{{ postPopup.status }}</span>
            <button class="cmp-pop-close" aria-label="Close" @click="postPopup = null">✕</button>
          </div>
          <p class="cmp-pop-when">{{ fmt(postPopup.scheduled_at) }} · {{ postPopup.campaign_topic }}</p>
          <p class="cmp-pop-content">{{ postPopup.content }}</p>
          <div class="cmp-pop-actions">
            <button class="cmp-btn cmp-btn-primary" data-testid="popup-open-campaign" @click="openFromPopup">Open campaign results</button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, onUnmounted, computed, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useCampaignsStore } from '../stores/campaigns'
import CampaignCalendar from '../components/CampaignCalendar.vue'
import CampaignChat from '../components/CampaignChat.vue'
import CampaignResults from '../components/CampaignResults.vue'

const route = useRoute()
const store = useCampaignsStore()

const siteId = computed(() => route.params.id)
const showForm = ref(false)
const selectedId = ref(null)
const postPopup = ref(null)

const form = reactive({ detail: '', objective: 'engagement', targetValue: null, targetWindowDays: null })
const objectiveLabel = (o) => ({ impressions: 'Impressions', engagement: 'Engagement', followers: 'Followers' }[o] || o)
const fmt = (v) => (v ? new Date(v).toLocaleString() : '—')
const shortDate = (v) => (v ? new Date(v).toLocaleString([], { month: 'short', day: 'numeric' }) : '—')

// Calendar limited to the selected campaign (its waves + posts).
const calendarPosts = computed(() =>
  selectedId.value ? store.calendar.filter((p) => p.campaign_id === selectedId.value) : store.calendar)

// Dashboard numbers, summed from the loaded reports (measured metrics only).
const stats = computed(() => {
  const c = store.current
  const waves = c?.waves || []
  const reports = waves.map((w) => store.reports[w.id]).filter(Boolean)
  const allMetricPosts = reports.flatMap((r) => r.posts || [])
  const sum = (key) => allMetricPosts.reduce((n, p) => n + (p[key] || 0), 0)
  const impressions = sum('impressions')
  // Engagement = comments + shares + clicks (Postiz exposes no likes/reactions).
  const engagement = sum('comments') + sum('shares') + sum('clicks')
  const posts = waves.reduce((n, w) => n + (w.posts?.length || 0), 0)
  const channels = new Set(waves.flatMap((w) => (w.posts || []).map((p) => p.channel))).size

  // Best channel across all reports, by summed score.
  const byCh = {}
  for (const r of reports) for (const ch of (r.byChannel || [])) byCh[ch.key] = (byCh[ch.key] || 0) + ch.avg * ch.n
  const best = Object.entries(byCh).sort((a, b) => b[1] - a[1])[0]
  const bestChannel = best && best[1] > 0 ? best[0] : '—'

  // Next recalibration = earliest future observe_to among live waves.
  const nowMs = Date.now()
  const futureEnds = waves
    .filter((w) => ['scheduled', 'observing'].includes(w.status) && w.observe_to)
    .map((w) => new Date(w.observe_to).getTime())
    .filter((t) => t >= nowMs)
    .sort((a, b) => a - b)
  const nextRecal = futureEnds.length ? shortDate(new Date(futureEnds[0])) : '—'

  return {
    impressions, engagement, posts, channels,
    waves: waves.length,
    status: c?.status || '—',
    bestChannel, nextRecal,
  }
})

function openCampaign(id) {
  selectedId.value = id
  store.loadCampaign(id)
}
function showPostPopup(post) { postPopup.value = post }
async function onApprove({ waveId, startInDays }) {
  if (!waveId) return
  const ok = window.confirm('This schedules real posts to your connected channels in Postiz. They publish at their scheduled time. Continue?')
  if (!ok) return
  await store.approveWave(selectedId.value, waveId, { startInDays: startInDays ?? 1 })
}
async function onGenerate(guidance = '') { await store.generateWave(selectedId.value, guidance) }
async function onCollect({ waveId }) { if (waveId) await store.observe(selectedId.value, waveId) }
function openFromPopup() {
  const p = postPopup.value
  postPopup.value = null
  if (p) openCampaign(p.campaign_id)
}

async function submitCreate() {
  const created = await store.create(siteId.value, { ...form })
  if (created) {
    Object.assign(form, { detail: '', objective: 'engagement', targetValue: null, targetWindowDays: null })
    showForm.value = false
    openCampaign(created.id)
  }
}

// Keep the calendar in sync after actions (approve schedules new posts).
watch(() => store.current && store.current.waves && store.current.waves.map((w) => w.status).join(','),
  () => { store.loadCalendar(siteId.value) })

async function boot(id) {
  await store.loadList(id)
  store.loadCalendar(id)
  if (!selectedId.value && store.list.length) openCampaign(store.list[0].id)
}

const onKey = (e) => { if (e.key === 'Escape' && postPopup.value) postPopup.value = null }
onMounted(() => { boot(siteId.value); document.addEventListener('keydown', onKey) })
onUnmounted(() => document.removeEventListener('keydown', onKey))
watch(siteId, (id) => { if (id) { selectedId.value = null; postPopup.value = null; store.clearCurrent(); boot(id) } })
</script>

<style scoped>
/* Editor-style full-height two-pane: chat sidebar + main. */
.cmp-editor { display: grid; grid-template-columns: minmax(320px, 400px) 1fr; height: calc(100vh - 52px); overflow: hidden; color: var(--text-primary); }

.cmp-side { display: flex; flex-direction: column; min-height: 0; border-right: var(--glass-border); background: var(--surface-1, var(--glass-bg)); }
.cmp-side-top { display: flex; align-items: center; justify-content: space-between; gap: 10px; padding: 14px 16px; border-bottom: var(--glass-border); flex-shrink: 0; }
.cmp-side-title { font-size: 1rem; font-weight: 700; }
.cmp-side-form { padding: 14px 16px; border-bottom: var(--glass-border); display: flex; flex-direction: column; gap: 10px; flex-shrink: 0; }
.cmp-form-lead { font-size: 0.8rem; color: var(--text-secondary); margin: 0; line-height: 1.5; }
.cmp-form-row { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
.cmp-side-list { display: flex; flex-direction: column; gap: 6px; padding: 12px 12px 0; flex-shrink: 0; overflow-y: auto; max-height: 32%; }
.cmp-side-chat { flex: 1; min-height: 0; display: flex; }
.cmp-side-empty { color: var(--text-secondary); font-size: 0.85rem; padding: 24px 8px; text-align: center; margin: auto; }

.cmp-main { overflow-y: auto; padding: 20px; display: flex; flex-direction: column; gap: 16px; min-width: 0; }
.cmp-main .cmp-card { margin-bottom: 0; }
.cmp-main-empty { margin: auto; text-align: center; max-width: 440px; padding: 40px 16px; }
.cmp-main-empty-title { font-size: 1.1rem; font-weight: 700; margin: 0 0 8px; }

.cmp-card {
  background: var(--glass-bg); border: var(--glass-border); border-radius: var(--radius-md);
  padding: 16px; color: var(--text-primary); margin-bottom: 16px;
  backdrop-filter: var(--glass-blur); -webkit-backdrop-filter: var(--glass-blur); box-shadow: var(--elev-sm);
}
.cmp-section-label { font-size: 0.72rem; text-transform: uppercase; letter-spacing: 0.08em; color: var(--text-secondary); margin-bottom: 12px; }
.cmp-btn-sm { padding: 6px 12px; font-size: 0.8rem; }

@media (max-width: 860px) {
  .cmp-editor { grid-template-columns: 1fr; height: auto; overflow: visible; }
  .cmp-side { border-right: none; border-bottom: var(--glass-border); height: 72vh; }
}

/* Tabs */
.cmp-tabs { display: inline-flex; gap: 2px; background: var(--glass-bg); border: var(--glass-border); border-radius: var(--radius-sm); padding: 2px; margin-bottom: 16px; }
.cmp-tab { padding: 8px 16px; border-radius: 6px; background: transparent; border: none; color: var(--text-secondary); font-size: 0.85rem; font-weight: 600; cursor: pointer; transition: all 0.15s; }
.cmp-tab.active { background: var(--glass-bg-active); color: var(--text-primary); }
.cmp-tab:hover:not(.active) { color: var(--text-primary); }

/* Stat cards */
.cmp-stats { display: grid; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); gap: 12px; margin-bottom: 16px; }
.cmp-stat {
  background: var(--glass-bg); border: var(--glass-border); border-radius: var(--radius-md);
  padding: 14px 16px; display: flex; flex-direction: column; gap: 4px;
  backdrop-filter: var(--glass-blur); -webkit-backdrop-filter: var(--glass-blur); box-shadow: var(--elev-sm);
}
.cmp-stat-val { font-size: 1.5rem; font-weight: 700; color: var(--text-primary); font-variant-numeric: tabular-nums; line-height: 1.1; }
.cmp-stat-accent { color: var(--accent-light); text-transform: capitalize; font-size: 1.15rem; }
.cmp-stat-label { font-size: 0.72rem; color: var(--text-secondary); }

.cmp-form-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(160px, 1fr)); gap: 12px; }
.cmp-field { display: flex; flex-direction: column; gap: 4px; }
.cmp-field-wide { grid-column: 1 / -1; }
.cmp-label { font-size: 0.72rem; color: var(--text-secondary); }
.cmp-input {
  width: 100%; border: var(--glass-border); border-radius: var(--radius-sm); padding: 8px 10px;
  font-size: 0.875rem; background: var(--glass-bg-active); color: var(--text-primary);
  font-family: inherit; outline: none; color-scheme: dark; transition: border 0.15s, box-shadow 0.15s;
}
.cmp-input::placeholder { color: var(--text-muted); }
.cmp-input:focus { border: var(--glass-border-hi); box-shadow: 0 0 0 3px var(--accent-soft); }
.cmp-textarea { resize: vertical; }
.cmp-form-actions { margin-top: 14px; }

.cmp-btn { border-radius: var(--radius-sm); padding: 8px 14px; font-size: 0.875rem; font-weight: 600; cursor: pointer; border: 1px solid transparent; transition: all 0.15s; }
.cmp-btn-primary { background: var(--accent); color: #fff; box-shadow: 0 0 18px var(--accent-glow); }
.cmp-btn-primary:hover { background: var(--accent-light); }
.cmp-btn-primary:disabled { opacity: 0.55; cursor: not-allowed; box-shadow: none; }

.cmp-alert { color: var(--danger); font-size: 0.85rem; margin: 10px 0; }
.cmp-muted { color: var(--text-secondary); font-size: 0.85rem; margin: 8px 0; }
.cmp-hint { color: var(--text-secondary); font-size: 0.85rem; text-align: center; margin: 16px 0 4px; }
.cmp-field-hint { color: var(--text-muted); font-size: 0.72rem; margin-top: 4px; }

/* Campaign chips */
.cmp-chip-row { display: flex; gap: 8px; flex-wrap: wrap; margin-bottom: 16px; }
.cmp-cchip {
  display: inline-flex; align-items: center; gap: 8px; padding: 7px 12px; cursor: pointer;
  background: var(--glass-bg); border: var(--glass-border); border-radius: 999px;
  color: var(--text-primary); font-size: 0.82rem; font-weight: 600; transition: background 0.15s, border-color 0.15s;
}
.cmp-cchip:hover { background: var(--glass-bg-hover); }
.cmp-cchip.active { border: var(--glass-border-hi); background: var(--glass-bg-active); }
.cmp-side-list .cmp-cchip { width: 100%; justify-content: flex-start; border-radius: var(--radius-sm); }
.cmp-cchip-name { flex: 1; text-align: left; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.cmp-cchip-dot { width: 7px; height: 7px; border-radius: 50%; background: var(--text-muted); flex: none; }
.cmp-cchip-dot[data-status='active'] { background: var(--success); }
.cmp-cchip-dot[data-status='draft'] { background: var(--warning); }
.cmp-cchip-obj { font-size: 0.7rem; color: var(--text-secondary); font-weight: 500; }

/* Chips + pills (post popup) */
.cmp-chip { font-size: 0.72rem; font-weight: 600; border-radius: 999px; padding: 2px 9px; background: var(--glass-bg-active); color: var(--text-secondary); }
.cmp-chip-channel { color: var(--accent-light); text-transform: capitalize; }
.cmp-pill { font-size: 0.68rem; font-weight: 700; border-radius: 999px; padding: 2px 9px; background: var(--glass-bg-active); color: var(--text-secondary); text-transform: capitalize; white-space: nowrap; }
.cmp-pill[data-status='scheduled'], .cmp-pill[data-status='published'] { background: rgba(52,211,153,0.18); color: var(--success); }
.cmp-pill[data-status='draft'] { background: rgba(251,191,36,0.18); color: var(--warning); }
.cmp-pill[data-status='failed'] { background: rgba(248,113,113,0.18); color: var(--danger); }

/* Post preview popup */
.cmp-pop-backdrop {
  position: fixed; inset: 0; z-index: 300; background: rgba(0,0,0,0.55);
  backdrop-filter: blur(2px); display: flex; align-items: center; justify-content: center; padding: 20px;
}
.cmp-pop {
  width: min(520px, 94vw); max-height: 82vh; overflow-y: auto;
  background: var(--surface-base, #12121c); border: var(--glass-border-hi); border-radius: var(--radius-md);
  box-shadow: var(--elev-lg); padding: 18px;
}
.cmp-pop-head { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; margin-bottom: 8px; }
.cmp-pop-angle { font-size: 0.75rem; font-style: italic; color: var(--text-secondary); }
.cmp-pop-close { margin-left: auto; background: none; border: none; color: var(--text-secondary); font-size: 1rem; cursor: pointer; }
.cmp-pop-close:hover { color: var(--text-primary); }
.cmp-pop-when { font-size: 0.78rem; color: var(--text-secondary); margin: 0 0 12px; }
.cmp-pop-content { color: var(--text-primary); font-size: 0.92rem; line-height: 1.6; white-space: pre-wrap; margin: 0 0 16px; }
.cmp-pop-actions { display: flex; justify-content: flex-end; }

.cmp-pop-enter-active, .cmp-pop-leave-active { transition: opacity 0.16s ease; }
.cmp-pop-enter-active .cmp-pop, .cmp-pop-leave-active .cmp-pop { transition: transform 0.18s cubic-bezier(0.16,1,0.3,1); }
.cmp-pop-enter-from, .cmp-pop-leave-to { opacity: 0; }
.cmp-pop-enter-from .cmp-pop, .cmp-pop-leave-to .cmp-pop { transform: scale(0.96); }
@media (prefers-reduced-motion: reduce) {
  .cmp-pop-enter-active, .cmp-pop-leave-active,
  .cmp-pop-enter-active .cmp-pop, .cmp-pop-leave-active .cmp-pop { transition: none; }
}

@media (max-width: 820px) { .cmp-header { flex-direction: column; } }
</style>
